import React from "react";
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  Check,
  Coins,
  ExternalLink,
  MessageCircle,
  ShieldCheck,
  Star,
  TrendingUp,
  Users,
} from "lucide-react";
import { DISCORD_LINK, LOGO_URL, PATREON_LINK } from "@/constants";
import Footer from "@/components/Footer";

const communityFeatures = [
  { label: "US Stocks Research", icon: TrendingUp },
  { label: "PSX Research", icon: BarChart3 },
  { label: "Crypto Market Updates", icon: Coins },
  { label: "Economic Calendar Alerts", icon: CalendarDays },
  { label: "Community Discussions", icon: MessageCircle },
];

const resultImages = [
  {
    src: "/landing-assets/smci-result.png",
    alt: "ICS SMCI winning call result",
    title: "SMCI Result",
    meta: "+68% profit example",
  },
  {
    src: "/landing-assets/anf-result.png",
    alt: "ICS ANF winning call result",
    title: "ANF Result",
    meta: "+11% gains example",
  },
  {
    src: "/landing-assets/crwd-result.png",
    alt: "ICS CRWD winning call result",
    title: "CRWD Result",
    meta: "61% gains example",
  },
  {
    src: "/landing-assets/crdo-result.png",
    alt: "ICS CRDO target hit result",
    title: "CRDO Result",
    meta: "Target hit example",
  },
];

const testimonials = [
  "Clear calls, clear targets, and a serious community around the research.",
  "The Discord helps me follow US, PSX, and market updates in one place.",
  "ICS keeps the focus on Shariah-conscious research and disciplined strategy.",
];

const premiumFeatures = [
  "Premium Discord rooms",
  "Detailed research write-ups",
  "Short-term trade call alerts",
  "Deeper Q&A with the ICS team",
];

const JoinDiscordLanding = () => {
  return (
    <div className="min-h-screen bg-ics-navy text-ics-white">
      <header className="sticky top-0 z-50 border-b border-white/5 bg-ics-navy/95 backdrop-blur-lg">
        <div className="mx-auto grid max-w-7xl grid-cols-[auto_1fr] items-center gap-4 px-4 py-3 sm:px-6 md:grid-cols-[auto_1fr_auto] lg:px-8">
          <a href="/" className="flex items-center" data-testid="join-landing-logo">
            <img
              src={LOGO_URL}
              alt="Investment Circle Shariah"
              className="h-14 w-auto md:h-16"
            />
          </a>

          <p className="hidden justify-self-center text-center text-sm font-semibold text-ics-gray md:block">
            Shariah-first investing community for US and Pakistan stocks
          </p>

          <a
            href={DISCORD_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="join-landing-nav-cta"
            className="inline-flex items-center justify-center gap-2 justify-self-end rounded-full bg-ics-orange px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:scale-105 hover:bg-ics-orange-hover"
          >
            Join Free Discord
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </header>

      <main>
        <section
          id="home"
          data-testid="join-landing-hero"
          className="relative overflow-hidden bg-[radial-gradient(ellipse_at_top_right,_rgba(34,197,94,0.16),_rgba(2,6,23,0)_34%),radial-gradient(ellipse_at_top_left,_rgba(249,115,22,0.18),_rgba(2,6,23,0)_38%),linear-gradient(180deg,_#020617_0%,_#0F172A_100%)] px-4 py-16 sm:px-6 md:py-24 lg:px-8"
        >
          <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1fr_0.9fr]">
            <div className="animate-fade-in-up">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-ics-orange/25 bg-ics-orange/10 px-4 py-2 text-sm font-semibold text-ics-orange">
                <Users className="h-4 w-4" />
                Free ICS Community
              </div>

              <h1 className="max-w-4xl font-heading text-4xl font-bold leading-tight text-ics-white sm:text-5xl lg:text-6xl">
                Join the Free ICS Shariah Compliance Community
              </h1>

              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ics-gray md:text-xl">
                Get market research, alerts, and community discussion inside the
                free ICS Discord.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {communityFeatures.map(({ label, icon: Icon }) => (
                  <div
                    key={label}
                    className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3"
                  >
                    <Icon className="h-5 w-5 flex-shrink-0 text-ics-orange" />
                    <span className="text-sm font-semibold text-ics-white">
                      {label}
                    </span>
                  </div>
                ))}
              </div>

              <a
                href={DISCORD_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="join-landing-hero-discord"
                className="cta-primary mt-9 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ics-orange px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:scale-105 hover:bg-ics-orange-hover sm:w-auto sm:text-lg"
              >
                Join Free Discord
                <ArrowRight className="h-5 w-5" />
              </a>

              <a
                href={PATREON_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="join-landing-hero-premium"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-8 py-4 text-base font-semibold text-ics-white transition-all duration-300 hover:border-ics-orange/40 hover:bg-white/10 sm:ml-4 sm:mt-9 sm:w-auto sm:text-lg"
              >
                Join Premium
                <ExternalLink className="h-5 w-5" />
              </a>

              <p className="mt-5 max-w-xl text-xs leading-relaxed text-ics-gray">
                Educational research and community discussion only. Past results
                are examples and do not guarantee future performance.
              </p>
            </div>

            <div className="glass-card animate-slide-in-right overflow-hidden rounded-2xl">
              <img
                src="/landing-assets/smci-result.png"
                alt="ICS recent winning call example"
                className="w-full bg-ics-navy-mid object-contain"
              />
            </div>
          </div>
        </section>

        <section className="border-y border-white/5 bg-ics-navy-mid px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-6xl gap-4 sm:grid-cols-3">
            {[
              ["Recent winning calls", TrendingUp],
              ["Research screenshots", ShieldCheck],
              ["Discord screenshots", MessageCircle],
            ].map(([label, Icon]) => (
              <div key={label} className="flex items-center justify-center gap-3 text-ics-gray">
                <Icon className="h-5 w-5 text-ics-orange" />
                <span className="text-sm font-semibold">{label}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-ics-navy px-4 py-16 sm:px-6 md:py-24 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 max-w-3xl">
              <div className="section-divider" />
              <h2 className="font-heading text-3xl font-bold text-ics-white md:text-5xl">
                Performance Highlights
              </h2>
              <p className="mt-4 text-ics-gray">
                Recent examples from ICS calls, Discord posts, and chart results.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {resultImages.map((image) => (
                <article
                  key={image.src}
                  className="glass-card overflow-hidden rounded-2xl transition-transform duration-300 hover:-translate-y-1"
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="h-auto w-full bg-ics-navy-mid object-contain"
                    loading="lazy"
                  />
                  <div className="flex items-center justify-between gap-4 border-t border-white/5 px-5 py-4">
                    <div>
                      <h3 className="font-heading text-xl font-semibold text-ics-white">
                        {image.title}
                      </h3>
                      <p className="mt-1 text-sm text-ics-gray">{image.meta}</p>
                    </div>
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-green-500/15">
                      <Check className="h-5 w-5 text-green-400" />
                    </span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-ics-navy px-4 py-16 sm:px-6 md:py-20 lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <div className="section-divider" />
              <h2 className="font-heading text-3xl font-bold text-ics-white md:text-4xl">
                Want the Premium Calls?
              </h2>
              <p className="mt-4 max-w-2xl text-ics-gray">
                Join the free Discord first, then upgrade through Patreon when
                you want premium rooms, deeper research, and trade call access.
              </p>
            </div>

            <div className="glass-card rounded-2xl p-7 md:p-8">
              <div className="grid gap-4 sm:grid-cols-2">
                {premiumFeatures.map((feature) => (
                  <div key={feature} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-ics-orange" />
                    <span className="text-sm font-semibold text-ics-gray">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={PATREON_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="join-landing-premium-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-ics-orange px-7 py-3.5 font-semibold text-white transition-all duration-300 hover:bg-ics-orange-hover"
                >
                  Buy Premium on Patreon
                  <ExternalLink className="h-4 w-4" />
                </a>
                <a
                  href={DISCORD_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-7 py-3.5 font-semibold text-ics-white transition-all duration-300 hover:border-white/20 hover:bg-white/10"
                >
                  Join Free First
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-ics-navy-mid px-4 py-16 sm:px-6 md:py-20 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 max-w-3xl">
              <div className="section-divider" />
              <h2 className="font-heading text-3xl font-bold text-ics-white md:text-4xl">
                Community Testimonials
              </h2>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              {testimonials.map((quote, index) => (
                <figure key={quote} className="glass-card rounded-2xl p-6">
                  <div className="mb-4 flex gap-1 text-ics-orange">
                    {[0, 1, 2, 3, 4].map((star) => (
                      <Star key={star} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-sm leading-relaxed text-ics-gray">
                    "{quote}"
                  </blockquote>
                  <figcaption className="mt-5 text-sm font-semibold text-ics-white">
                    ICS Community Member {index + 1}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="section-gradient px-4 py-16 sm:px-6 md:py-24 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="font-heading text-4xl font-bold text-ics-white md:text-5xl">
              Ready to Join?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-ics-gray">
              Enter the free ICS Discord and start following the community today.
            </p>
            <a
              href={DISCORD_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="join-landing-bottom-discord"
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ics-orange px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:scale-105 hover:bg-ics-orange-hover sm:w-auto sm:text-lg"
            >
              Join Free Discord
              <ArrowRight className="h-5 w-5" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default JoinDiscordLanding;
